import type { DashboardSummary } from '@/hooks/queries/dashboards/useDashboardList'
import dayjs from '@/lib/dayjs'
import { type OrgSegment, type TenantSegment, dashboardViewPath, toTenantSegment } from '@/lib/routeScope'
import { formatDisplayDate } from '@/utils/dateUtils'
import { type ClassValue, clsx } from 'clsx'
import Papa from 'papaparse'
import { useCallback, useState } from 'react'
import { type ExternalToast, toast } from 'sonner'
import { extendTailwindMerge } from 'tailwind-merge'

/**
 * The `--text-*` theme tokens in index.css generate font-size utilities, but tailwind-merge cannot
 * tell `text-body` from a colour, so it filed them under text-color and dropped the size whenever a
 * `text-*` colour sat beside one in the same `cn`. Registering them as sizes restores that, and makes
 * a caller's size class beat a component's base size instead of leaving the stylesheet order to
 * decide. Keep this list in step with the `--text-*` tokens under `@theme inline`.
 */
const TEXT_SIZE_TOKENS = ['badge', 'control', 'body', 'company-name', 'tenant-name', 'form-modal-title', 'table']

const twMerge = extendTailwindMerge({ extend: { classGroups: { 'font-size': [{ text: TEXT_SIZE_TOKENS }] } } })

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs))
}

/**
 * Stops Ctrl+A / Cmd+A from bubbling out of floating UI (dialogs, popovers)
 * and triggering select-all on background table rows.
 */
export function stopSelectAllPropagation(e: React.KeyboardEvent) {
	if ((e.ctrlKey || e.metaKey) && (e.key === 'a' || e.key === 'A')) {
		e.stopPropagation()
	}
}

/**
 * Runs async actions in parallel via Promise.allSettled and returns success/failure counts.
 * Actions are wrapped in functions so we control when they run
 */
export async function runBulkAction(
	actions: (() => Promise<void>)[],
): Promise<{ successCount: number; failCount: number }> {
	const results = await Promise.allSettled(actions.map(a => a()))
	const successCount = results.filter(r => r.status === 'fulfilled').length
	const failCount = results.length - successCount
	return { successCount, failCount }
}

export const formatTimestamp = (timestamp: string | number): string => {
	const utcTime = dayjs.utc(timestamp)
	const localTime = utcTime.local()
	return localTime.fromNow()
}

export const getTimestampTooltip = (timestamp: string | number): string => {
	return formatDisplayDate(timestamp, 'YYYY-MM-DD, HH:mm:ss')
}

export function formatCurrency(amount: number, currency = 'USD', locale = 'en-US') {
	return new Intl.NumberFormat(locale, {
		style: 'currency',
		currency,
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amount)
}

export function formatDate(date: string | number | Date, locale = 'en-US', options?: Intl.DateTimeFormatOptions) {
	const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date
	return d.toLocaleDateString(locale, options || { year: 'numeric', month: 'short', day: 'numeric' })
}

/** Device state columns holding a time: `heartbeat`, and any key naming a timestamp. */
export const isTimestampStateKey = (key: string): boolean => key === 'heartbeat' || key.includes('timestamp')

export const formatDeviceStateValue = (key: string, value: unknown): string => {
	if (value === null || value === undefined) {
		return '-'
	}
	if (typeof value === 'number') {
		return value.toLocaleString()
	}
	if (isTimestampStateKey(key)) {
		return formatDisplayDate(value as string | number | Date, 'DD/MM/YYYY, HH:mm:ss')
	}
	return String(value)
}

export function formatDuration(seconds: number): string {
	const dur = dayjs.duration(seconds, 'seconds')
	const yrs = dur.years()
	const mons = dur.months()
	const d = dur.days()
	const hrs = dur.hours()
	const mins = dur.minutes()
	const secs = dur.seconds()
	const millisecs = dur.milliseconds()
	const decimalSecs = secs + millisecs / 1000

	let result = ''
	if (yrs > 0) {
		result += `${yrs} yr${yrs > 1 ? 's' : ''} `
	}
	if (mons > 0) {
		result += `${mons} mo${mons > 1 ? 's' : ''} `
	}
	if (d > 0) {
		result += `${d} d `
	}
	if (hrs > 0) {
		result += `${hrs} hr${hrs > 1 ? 's' : ''} `
	}
	if (mins > 0) {
		result += `${mins} min `
	}
	if (decimalSecs > 0) {
		result += `${Number(decimalSecs.toFixed(3))} sec`
	}
	if (!result) {
		result = '0 sec'
	}

	return result.trim()
}

/**
 * Saves a blob to the user's disk.
 *
 * Both fiddly parts are load-bearing. The anchor has to be in the document for some browsers to
 * honour a click on it, and revoking the object URL in the same tick aborts the download in Safari
 * and WebKit, so the revoke waits until the download has started.
 */
export function triggerBlobDownload(blob: Blob, filename: string): void {
	const url = URL.createObjectURL(blob)
	const anchor = document.createElement('a')
	anchor.href = url
	anchor.download = filename
	document.body.appendChild(anchor)
	anchor.click()
	document.body.removeChild(anchor)
	setTimeout(() => URL.revokeObjectURL(url), 0)
}

export function downloadAsJson(data: unknown, filename: string): void {
	const json = JSON.stringify(data, null, 2)
	triggerBlobDownload(new Blob([json], { type: 'application/json' }), filename)
}

export async function downloadAsZip(
	files: Array<{ filename: string; data: unknown }>,
	zipFilename: string,
): Promise<void> {
	const { downloadZip } = await import('client-zip')
	const inputs = files.map(file => ({
		name: file.filename,
		input: JSON.stringify(file.data, null, 2),
	}))
	triggerBlobDownload(await downloadZip(inputs).blob(), zipFilename)
}

export const convertToCSVAndDownload = (data: Record<string, unknown>[], filename: string): void => {
	if (!data.length) {
		return
	}
	const csvData = Papa.unparse(data, {
		delimiter: ',',
		header: true,
		skipEmptyLines: true,
	})
	triggerBlobDownload(new Blob([csvData], { type: 'text/csv;charset=utf-8;' }), filename)
}

export async function relabelCsvColumn(blob: Blob, oldKey: string, newKey: string): Promise<Blob> {
	if (oldKey === newKey) {
		return blob
	}
	const text = await blob.text()
	const parsed = Papa.parse<Record<string, unknown>>(text, { header: true, skipEmptyLines: true })
	if (!parsed.meta.fields?.includes(oldKey)) {
		return blob
	}
	const fields = parsed.meta.fields.map(field => (field === oldKey ? newKey : field))
	const rows = parsed.data.map(({ [oldKey]: value, ...rest }) => ({ [newKey]: value, ...rest }))
	const csvData = Papa.unparse(rows, { columns: fields, header: true })
	return new Blob([csvData], { type: 'text/csv;charset=utf-8;' })
}

/**
 * Inserts `column` just before `sourceColumn`, deriving each cell from that row's source value.
 * `renameSourceTo` renames the source column in the same pass, so the file is parsed only once.
 */
export async function insertCsvColumn(
	blob: Blob,
	{
		sourceColumn,
		column,
		valueFor,
		renameSourceTo,
	}: {
		sourceColumn: string
		column: string
		valueFor: (sourceValue: string) => string
		renameSourceTo?: string
	},
): Promise<Blob> {
	const text = await blob.text()
	const parsed = Papa.parse<Record<string, unknown>>(text, { header: true, skipEmptyLines: true })
	const sourceIndex = parsed.meta.fields?.indexOf(sourceColumn) ?? -1
	if (sourceIndex === -1 || !parsed.meta.fields) {
		return blob
	}
	// A name another column already uses would collapse the two into one cell, so skip that half
	// of the work rather than corrupting the file: the rename can still apply without the insert.
	const otherNames = new Set(parsed.meta.fields.filter(field => field !== sourceColumn))
	const shouldInsert = !otherNames.has(column)
	const sourceLabel =
		renameSourceTo && renameSourceTo !== column && !otherNames.has(renameSourceTo) ? renameSourceTo : sourceColumn
	if (!shouldInsert && sourceLabel === sourceColumn) {
		return blob
	}
	const fields = parsed.meta.fields.map(field => (field === sourceColumn ? sourceLabel : field))
	if (shouldInsert) {
		fields.splice(sourceIndex, 0, column)
	}
	const rows = parsed.data.map(({ [sourceColumn]: sourceValue, ...rest }) => ({
		...(shouldInsert ? { [column]: valueFor(String(sourceValue ?? '')) } : {}),
		[sourceLabel]: sourceValue,
		...rest,
	}))
	const csvData = Papa.unparse(rows, { columns: fields, header: true })
	return new Blob([csvData], { type: 'text/csv;charset=utf-8;' })
}

export function parseCSV<T = Record<string, unknown>>(file: File): Promise<T[]> {
	return new Promise((resolve, reject) => {
		Papa.parse<T>(file, {
			header: true,
			skipEmptyLines: true,
			complete: result => resolve(result.data),
			error: error => reject(error),
		})
	})
}

export interface CopyToClipboardOptions {
	label?: string
	successMessage?: string
	errorMessage?: string
	showToast?: boolean
}

export const handleCopyToClipboard = async (text: string, options: CopyToClipboardOptions = {}): Promise<boolean> => {
	const { label, successMessage, errorMessage, showToast = true } = options
	const defaultSuccessMessage = label ? `${label} copied to clipboard` : 'Copied to clipboard'
	const defaultErrorMessage = 'Failed to copy to clipboard'

	try {
		if (navigator.clipboard?.writeText) {
			await navigator.clipboard.writeText(text)
		} else {
			const textArea = document.createElement('textarea')
			textArea.value = text
			textArea.style.position = 'fixed'
			textArea.style.left = '-9999px'
			textArea.style.top = '-9999px'
			document.body.appendChild(textArea)
			textArea.focus()
			textArea.select()
			document.execCommand('copy')
			document.body.removeChild(textArea)
		}
		if (showToast) {
			toast.success(successMessage ?? defaultSuccessMessage)
		}
		return true
	} catch (error) {
		console.error('Failed to copy to clipboard:', error)
		if (showToast) {
			toast.error(errorMessage ?? defaultErrorMessage)
		}
		return false
	}
}

export const copyIdentifiersToClipboard = async (
	identifiers: string[],
	label = 'ID',
	options?: { separator?: string },
): Promise<boolean> => {
	if (!identifiers.length) {
		return false
	}
	const filteredIdentifiers = identifiers
		.filter(id => id != null)
		.map(id => String(id).trim())
		.filter(id => id.length > 0)
	if (!filteredIdentifiers.length) {
		return false
	}
	const uniqueIdentifiers = Array.from(new Set(filteredIdentifiers))
	const separator = options?.separator ?? ', '
	const joined = uniqueIdentifiers.join(separator)
	const text = `${label} : ${joined}`
	const preview = uniqueIdentifiers.slice(0, 20).join(separator)
	const suffix = uniqueIdentifiers.length > 20 ? ` +${uniqueIdentifiers.length - 20} more` : ''
	return handleCopyToClipboard(text, {
		label,
		successMessage: `${label} copied (${uniqueIdentifiers.length}): ${preview}${suffix}`,
	})
}

const COPIED_RESET_MS = 2000

export function useCopyToClipboard() {
	const [copied, setCopied] = useState<string | null>(null)
	const copy = useCallback(async (text: string, label?: string) => {
		const success = await handleCopyToClipboard(text, { label })
		if (success) {
			setCopied(text)
			setTimeout(() => setCopied(null), COPIED_RESET_MS)
		}
	}, [])
	return { copy, copied }
}

export const getDashboardLink = ({
	dashboardId,
	orgId,
	tenantId,
	dashboards,
	deviceId,
}: {
	dashboardId: string | null | undefined
	orgId: OrgSegment | undefined
	tenantId: TenantSegment | undefined
	dashboards: DashboardSummary[]
	deviceId?: string | null | undefined
}) => {
	if (!dashboardId || dashboardId === '' || !orgId) {
		return <span className='text-muted-foreground'>--</span>
	}

	const dashboard = dashboards.find(d => d.id.toString() === dashboardId)
	const dashboardName = dashboard?.title || `Dashboard ${dashboardId}`
	const devicePart = deviceId ? encodeURIComponent(deviceId) : 'select'
	const url = dashboardViewPath(orgId, tenantId ?? toTenantSegment(''), dashboardId, devicePart)

	return (
		<a
			href={url}
			target='_blank'
			rel='noopener noreferrer'
			className='text-primary hover:text-primary/90 hover:underline'>
			{dashboardName}
		</a>
	)
}

export const showSuccessToastWithAction = ({
	message,
	actionLabel,
	onAction,
	duration = 15000,
	...toastOptions
}: {
	message: string
	actionLabel: string
	onAction: () => void
	duration?: number
} & ExternalToast) => {
	toast.success(
		<div className='flex flex-col gap-1'>
			<span>{message}</span>
			<button
				type='button'
				className='text-primary hover:underline text-left text-body font-medium cursor-pointer'
				onClick={() => {
					onAction()
					toast.dismiss()
				}}>
				{actionLabel}
			</button>
		</div>,
		{ duration, ...toastOptions },
	)
}

export const showSessionRuleUpdatedToast = (ruleName: string) => {
	toast.success(
		<div className='flex flex-col gap-1'>
			<span className='text-body'>{`Updated session rule "${ruleName}"`}</span>
			<span className='text-warning text-control'>Updates will be applied automatically in 30 seconds</span>
		</div>,
	)
}

/**
 * Safely extracts a meaningful error message string from any caught error object
 * (native JS Errors, mapped ApiErrors, or raw Axios response shapes).
 */
export const getErrorMessage = (err: unknown): string => {
	if (err instanceof Error) {
		return err.message
	}
	if (err && typeof err === 'object') {
		if ('message' in err && typeof err.message === 'string') {
			return err.message
		}
		const errObj = err as {
			response?: { data?: { error_message?: string; message?: string; error?: string } }
			message?: string
		}
		const msg =
			errObj.response?.data?.error_message ||
			errObj.response?.data?.message ||
			errObj.response?.data?.error ||
			errObj.message
		if (msg && typeof msg === 'string') {
			return msg
		}
	}
	return String(err)
}

interface NumberTier {
	threshold: number
	symbol: string
}

const NUMBER_TIERS: NumberTier[] = [
	{ threshold: 1_000_000_000, symbol: 'B' },
	{ threshold: 1_000_000, symbol: 'M' },
	{ threshold: 1_000, symbol: 'k' },
]

/**
 * Formats a count into scalable compact notation (k, M, B).
 * - Exact values omit '+' (e.g. 50,000 -> '50k', 1,000,000 -> '1M', 1,200,000 -> '1.2M')
 * - In-between values append '+' (e.g. 50,123 -> '50k+', 1,234,567 -> '1.2M+')
 */
export function formatTabCount(count?: number | null, showPlus = true): string {
	if (count === undefined || count === null) {
		return '0'
	}
	if (count < 1000) {
		return String(count)
	}

	for (const tier of NUMBER_TIERS) {
		if (count >= tier.threshold) {
			const rawVal = count / tier.threshold
			const hasDecimal = tier.symbol !== 'k' && rawVal < 10 && Math.floor(rawVal * 10) % 10 !== 0
			const formattedVal = hasDecimal ? (Math.floor(rawVal * 10) / 10).toFixed(1) : String(Math.floor(rawVal))
			const step = hasDecimal ? tier.threshold / 10 : tier.threshold
			const isExact = count % step === 0
			return isExact || !showPlus ? `${formattedVal}${tier.symbol}` : `${formattedVal}${tier.symbol}+`
		}
	}

	return String(count)
}
